import { GetWorkOrderTasksQuery, useGetWorkOrderTasksQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import SlimBadge from '@/gf/components/SlimBadge'
import Spinner from '@/gf/components/Spinner'
import useToggle from '@/gf/hooks/useToggle'
import WorkOrderJob from '@/gf/modules/WorkOrderJob'
import { Disclosure } from '@headlessui/react'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import pluralize from 'pluralize'

type Job = GetWorkOrderTasksQuery['getWorkOrderTasks'][number]

// The card lives in the request page's narrow right-hand column, so the note
// preview is sized for that width — roughly two lines.
const NOTE_PREVIEW_LENGTH = 110

/** M5 sends notes as an array, and blank entries are common. */
const noteText = (job: Job) =>
  job.notes
    .map((note) => note.trim())
    .filter(Boolean)
    .join('\n')

const jobKey = (job: Job) => `${job.taskSeq ?? ''}-${job.taskId}`

const truncate = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, length).trimEnd()}…` : text

/** Note text clamped to about two lines, with the house Show more/less toggle. */
const JobNotes = ({ text }: { text: string }) => {
  const [expanded, expandedToggle] = useToggle()

  if (!text) return null
  if (text.length <= NOTE_PREVIEW_LENGTH)
    return <p className="text-sm text-gray-600 whitespace-pre-line break-words">{text}</p>

  return (
    <div className="text-sm text-gray-600">
      <span className="whitespace-pre-line break-words">
        {expanded ? text : truncate(text, NOTE_PREVIEW_LENGTH)}
      </span>{' '}
      <Action.T className="align-baseline" onClick={expandedToggle.toggle}>
        {expanded ? 'Show less' : 'Show more'}
      </Action.T>
    </div>
  )
}

/**
 * A damages job, shown in full: this is the one a coordinator has to act on.
 */
const DamageJob = ({ job }: { job: Job }) => (
  <li className="py-3 first:pt-0 last:pb-0 space-y-1">
    <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
      <span className="font-medium text-sm">{job.taskId}</span>
      <SlimBadge
        title="DAMAGES"
        size="sm"
        color="bg-red-600"
        textColor="text-white"
        className="font-medium tracking-wide"
      />
    </div>

    {job.description && <p className="text-sm text-gray-900">{job.description}</p>}

    <JobNotes text={noteText(job)} />
  </li>
)

/**
 * Every other job, on one line — code, reason, description. The description
 * takes whatever width is left and wraps to its own line rather than being
 * squeezed to nothing in a narrow column; notes stay behind the row's toggle.
 * The detail is there when someone wants it, but it never pushes the rest of
 * the request page off screen.
 */
const OtherJob = ({ job }: { job: Job }) => {
  const notes = noteText(job)
  const reason = WorkOrderJob.reasonLabelWithCode(job)
  const description = job.description?.trim() ?? ''

  const summary = (
    <div className="min-w-0 flex flex-row flex-wrap items-baseline gap-x-2 text-sm">
      <span className="font-medium shrink-0">{job.taskId}</span>
      {reason && <span className="text-xs text-gray-500 shrink-0">{reason}</span>}
      {description && (
        <span className="min-w-0 flex-1 basis-32 truncate text-gray-900" title={description}>
          {description}
        </span>
      )}
    </div>
  )

  if (!notes)
    return (
      <li className="py-1.5">
        <div className="pl-5">{summary}</div>
      </li>
    )

  return (
    <li className="py-1.5">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="w-full flex flex-row items-baseline gap-x-2 text-left">
              <ChevronRightIcon
                className={classNames(
                  'h-3 w-3 shrink-0 text-gray-400 transform duration-100',
                  open && 'rotate-90'
                )}
              />
              {summary}
            </Disclosure.Button>

            <Disclosure.Panel className="pl-5 pt-1">
              <p className="text-sm text-gray-600 whitespace-pre-line break-words">{notes}</p>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </li>
  )
}

/**
 * The jobs on an M5 work order, so a parts coordinator can see a damages job
 * without leaving Gearflow to open the work order in M5.
 *
 * A Primoris work order routinely carries a dozen jobs, and this card sits
 * beside the request, not instead of it — so it opens showing only what changes
 * the coordinator's decision (the damages jobs) and keeps the rest one click
 * away.
 *
 * Only rendered for orgs with an AssetWorks configuration and requests that
 * reference a work order — no feature flag. M5 being slow or unreachable must
 * never block the request page, so every failure degrades to a quiet line.
 */
const WorkOrderJobs = ({ workOrderNumber }: { workOrderNumber: string }) => {
  const client = useGqlClient()
  const { data, loading, error } = useGetWorkOrderTasksQuery({
    client,
    variables: { workOrderId: workOrderNumber },
  })

  const jobs = data?.getWorkOrderTasks ?? []
  const damageJobs = jobs.filter(WorkOrderJob.isDamage)
  const otherJobs = jobs.filter((job) => !WorkOrderJob.isDamage(job))

  return (
    <Card
      title="Jobs on this work order"
      subtitle={
        damageJobs.length > 0 ? (
          <p className="text-sm text-red-700">
            {damageJobs.length === 1
              ? '1 job is coded as damages — bill those parts to the job.'
              : `${damageJobs.length} jobs are coded as damages — bill those parts to the job.`}
          </p>
        ) : undefined
      }
    >
      <Card.Section>
        {loading ? (
          <div className="flex flex-row gap-x-2 items-center text-sm text-gray-600">
            <Spinner className="mx-0" size="xs" color="blue" />
            Loading jobs from M5…
          </div>
        ) : error ? (
          <p className="text-sm text-gray-500">
            Couldn&apos;t load jobs from M5. Check work order {workOrderNumber} in M5.
          </p>
        ) : jobs.length === 0 ? (
          // GEA-5927: no jobs means parts cannot be issued to this work order at all —
          // the job code is mandatory — so say what fixes it here, not just in the picker.
          <p className="text-sm text-gray-500">
            No jobs found in M5 for work order {workOrderNumber}. Parts can&apos;t be issued to it
            until a job is added in M5.
          </p>
        ) : (
          <>
            {damageJobs.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {damageJobs.map((job) => (
                  <DamageJob key={jobKey(job)} job={job} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600">
                {jobs.length} {pluralize('job', jobs.length)} on this work order — none coded
                damages.
              </p>
            )}

            {otherJobs.length > 0 && (
              <Disclosure>
                {({ open }) => (
                  <div
                    className={classNames(
                      'mt-3',
                      damageJobs.length > 0 && 'border-t border-gray-200 pt-3'
                    )}
                  >
                    <Disclosure.Button className="text-sm text-blue-500 underline">
                      {open
                        ? 'Show fewer jobs'
                        : `Show all ${jobs.length} ${pluralize('job', jobs.length)}`}
                    </Disclosure.Button>

                    <Disclosure.Panel>
                      <ul className="mt-1 divide-y divide-gray-200">
                        {otherJobs.map((job) => (
                          <OtherJob key={jobKey(job)} job={job} />
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            )}
          </>
        )}
      </Card.Section>
    </Card>
  )
}

export default WorkOrderJobs
